import type { ConversationDigestApiModel, ConversationHistoryResponse } from "@noa/api-core";
import type { QueryClient } from "@tanstack/react-query";
import { ConversationQueryKey } from "./query-keys";

/**
 * The row fields a client can keep current on its own, without asking the server: two it derives
 * from the live message list, and `name`, which the server generates but announces on the stream.
 */
type ConversationRowFields = Partial<Pick<ConversationDigestApiModel, "message_count" | "last_message_at" | "name">>;

/**
 * Update one conversation's row in every cached list page, without a refetch — for fields the caller
 * already knows. Lives here because it encodes this package's cache shape next to the key factory it
 * reads, and takes the values as an argument so the package stays store-free.
 *
 * No-ops when the lists aren't cached or the row is outside the cached pages, so callers may fire it
 * before the conversation exists server-side. Does not re-sort: only for fields that can't change
 * list order.
 */
export const updateConversationRow = (
  queryClient: QueryClient,
  sessionId: string,
  fields: ConversationRowFields
): void => {
  queryClient.setQueriesData<ConversationHistoryResponse>({ queryKey: ConversationQueryKey.lists }, (data) => {
    if (!data) return data;

    const index = data.conversations.findIndex((conversation) => conversation.session_id === sessionId);
    if (index === -1) return data;

    const conversations = [...data.conversations];
    conversations[index] = { ...conversations[index], ...fields };

    return { ...data, conversations };
  });
};
